import { describeApiError } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { Check, FileSpreadsheet, Upload, X } from 'lucide-react';
import { useEffect, useReducer, useRef, useState } from 'react';
import {
  capped,
  checkFileMeta,
  createImportRun,
  defaultFilter,
  fileStepLine,
  importAction,
  importReached,
  offersReviewAgain,
  partLine,
  partsNotice,
  progressLine,
  readImportFile,
  readWithLines,
  reviewCountsLine,
  reviewFilters,
  rowChanges,
  runSummary,
  splitIntoParts,
  templateHint,
  type ImportFile,
  type ImportPart,
  type ImportReview,
  type ImportRun,
  type PlayerImportTemplate,
  type ReviewFilter,
  type SaveOutcome,
} from '../../import/playerImport';
import { usePlayerImport, useRoomTemplates } from '../../queries';
import { btnGhost, btnPrimary, Modal } from '../Modal';

/**
 * Importing a room's players from a CSV (ADR 0019, docs/specs/player-import.md §9): choose a file, review what
 * every part would do, import, and read what landed. Every rule is in `import/playerImport.ts`; this renders it.
 * Nothing here holds a contact value — the server sends none (PI28) and the browser never shows the file's.
 */

type Step = 'file' | 'checking' | 'review' | 'saving' | 'done';

const STEPS: { key: Step; label: string }[] = [
  { key: 'file', label: 'File' },
  { key: 'review', label: 'Review' },
  { key: 'saving', label: 'Import' },
  { key: 'done', label: 'Done' },
];

const OUTCOME_WORDS: Record<string, string> = {
  create: 'New',
  update: 'Updating',
  conflict: 'Conflict',
  unchanged: 'Unchanged',
  error: 'Error',
};

function StepBar({ current }: { current: Step }) {
  const shown = current === 'checking' ? 'review' : current;
  const at = STEPS.findIndex((s) => s.key === shown);
  return (
    <ol className="mb-6 flex shrink-0 items-center gap-2 text-xs">
      {STEPS.map((step, index) => (
        <li key={step.key} className="flex items-center gap-2">
          <span
            className={`flex size-5 items-center justify-center rounded-full text-[10px] font-semibold ${
              index === at ? 'bg-felt-700 text-white' : index < at ? 'bg-felt-50 text-felt-700' : 'bg-ink-800/5 text-ink-800/40'
            }`}
          >
            {index < at ? <Check className="size-3" /> : index + 1}
          </span>
          <span className={index === at ? 'font-medium text-ink-950' : 'text-ink-800/50'}>{step.label}</span>
          {index < STEPS.length - 1 ? <span className="mx-1 h-px w-4 bg-ink-800/15" aria-hidden /> : null}
        </li>
      ))}
    </ol>
  );
}

/** PI78: which template the file was read with, and the columns it read. Nothing was written, so leaving is free. */
function ReadWithLine({ template, onOpenTemplates }: { template: PlayerImportTemplate; onOpenTemplates: () => void }) {
  const line = readWithLines(template);
  return (
    <div className="text-xs text-ink-800/70">
      {line.sentence ? (
        <p>
          {line.sentence}
          {line.linkToTemplates ? (
            <>
              {' '}
              <button type="button" onClick={onOpenTemplates} className="text-felt-700 underline hover:text-felt-900">
                Adapt it in Settings → Templates
              </button>
            </>
          ) : null}
        </p>
      ) : null}
      <p>{line.columns}</p>
    </div>
  );
}

export function PlayerImportModal({
  roomId,
  roomName,
  onClose,
  onOpenTemplates,
}: {
  roomId: string;
  roomName: string;
  onClose: () => void;
  /** Settings → Templates, where a room adapts the players template (PI78). */
  onOpenTemplates: () => void;
}) {
  const importer = usePlayerImport(roomId);
  // PI78's hint before a file is chosen. The preview's `Template` is the authority; this only says what to expect.
  const hint = templateHint(useRoomTemplates(roomId).data);
  const input = useRef<HTMLInputElement>(null);
  const runRef = useRef<ImportRun | null>(null);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  const [step, setStep] = useState<Step>('file');
  const [fileName, setFileName] = useState<string | null>(null);
  const [file, setFile] = useState<ImportFile | null>(null);
  const [parts, setParts] = useState<ImportPart[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [review, setReview] = useState<ImportReview | null>(null);
  const [checkFailure, setCheckFailure] = useState<string | null>(null);
  const [filter, setFilter] = useState<ReviewFilter>('update');
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);
  const [stopping, setStopping] = useState(false);

  // A closed dialog abandons a check in flight (it writes nothing, PI61); a save in flight is never abandoned —
  // `close` keeps the dialog open through one (PI62). Read through a ref: this runs once, when the dialog goes.
  const stepRef = useRef(step);
  stepRef.current = step;
  useEffect(() => () => {
    if (stepRef.current === 'checking') runRef.current?.cancel();
  }, []);

  const takeFile = (chosen: File) => {
    setProblems([]);
    setFile(null);
    setParts([]);
    setFileName(chosen.name);
    const refused = checkFileMeta(chosen.name, chosen.size);
    if (refused) {
      setProblems([refused]);
      return;
    }
    void chosen
      .text()
      .then((text) => {
        const read = readImportFile(text);
        if (!read.ok) {
          setProblems([read.problem]);
          return;
        }
        setFile(read.file);
        setParts(splitIntoParts(read.file));
      })
      .catch(() => setProblems(["That file couldn't be read."]));
  };

  /** A new run of the file: every part checked again under a new import id (PI56's recovery is this). */
  const check = async () => {
    if (!file) return;
    const run = createImportRun(file, parts, importer.api, {
      importId: crypto.randomUUID(),
      describe: (error) => describeApiError(error) ?? 'The server did not answer.',
      onChange: rerender,
      onSaved: importer.afterSave,
    });
    runRef.current = run;
    setReview(null);
    setCheckFailure(null);
    setOutcome(null);
    setStopping(false);
    setStep('checking');
    const result = await run.check();
    if (result.kind === 'cancelled') {
      setStep('file');
      return;
    }
    if (result.kind === 'failed') setCheckFailure(result.reason);
    else {
      setReview(result.review);
      setFilter(defaultFilter(result.review));
    }
    setStep('review');
  };

  const save = async () => {
    const run = runRef.current;
    if (!run) return;
    setStep('saving');
    setOutcome(await run.save());
    setStep('done');
  };

  /** Cancel while checking abandons; while saving it stops after the part in flight (PI61, PI62). */
  const cancelRun = () => {
    runRef.current?.cancel();
    if (step === 'saving') setStopping(true);
  };

  /** Closing during the save is Cancel: the dialog waits for the part in flight to answer (§9). */
  const close = () => {
    if (step === 'saving') cancelRun();
    else onClose();
  };

  const runParts = runRef.current?.parts() ?? [];
  const progress = progressLine(runParts);
  // At Done the summary above the list gives a failed part's reason, so the list does not repeat it.
  const partList = (
    <ul className="space-y-1 text-xs text-ink-800/70">
      {runParts.map((p) => (
        <li key={p.part} className={p.status === 'failed' ? 'text-chip-500' : undefined}>
          {partLine(p, step === 'done')}
        </li>
      ))}
    </ul>
  );

  let body = null;
  let footer = null;

  if (step === 'file') {
    body = (
      <div className="space-y-4">
        <p className="text-sm text-ink-800/70">{fileStepLine(hint)}</p>
        {fileName && file ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-felt-500/40 bg-felt-50/50 p-4">
            <FileSpreadsheet className="size-5 shrink-0 text-felt-700" />
            <div className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-ink-950">{fileName}</span>
              <span className="num block text-xs text-ink-800/60">
                {file.dataRows.toLocaleString('en-US')} {file.dataRows === 1 ? 'player' : 'players'}
                {parts.length > 1 ? ` · ${parts.length} parts` : ''}
              </span>
            </div>
            <button
              type="button"
              onClick={() => input.current?.click()}
              className="shrink-0 rounded-lg border border-ink-800/15 px-3 py-1.5 text-xs text-ink-800 hover:bg-white"
            >
              Choose another
            </button>
            <button
              type="button"
              onClick={() => {
                setFile(null);
                setFileName(null);
              }}
              aria-label={`Remove ${fileName}`}
              className="shrink-0 p-1 text-ink-800/40 hover:text-chip-500"
            >
              <X className="size-4" />
            </button>
          </div>
        ) : (
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const dropped = e.dataTransfer.files[0];
              if (dropped) takeFile(dropped);
            }}
            className="rounded-lg border-2 border-dashed border-ink-800/15 p-8 text-center"
          >
            <Upload className="mx-auto size-6 text-ink-800/30" />
            <p className="mt-2 text-sm text-ink-800/70">Drop a CSV file here, or</p>
            <button
              type="button"
              onClick={() => input.current?.click()}
              className="mt-2 rounded-lg border border-ink-800/15 px-4 py-2 text-sm text-ink-800 hover:bg-felt-50"
            >
              Choose a file
            </button>
            <p className="mt-3 text-xs text-ink-800/45">CSV only, up to 25,000 players. Nothing changes until you say so.</p>
          </div>
        )}
        {problems.length > 0 ? (
          <div className="space-y-1">
            {problems.slice(0, 20).map((problem) => (
              <ErrorNotice key={problem} message={problem} />
            ))}
            {problems.length > 20 ? (
              <p className="text-xs text-ink-800/60">And {problems.length - 20} more.</p>
            ) : null}
          </div>
        ) : null}
        <input
          ref={input}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const chosen = e.target.files?.[0];
            e.target.value = '';
            if (chosen) takeFile(chosen);
          }}
        />
      </div>
    );
    footer = (
      <>
        <button type="button" onClick={onClose} className={btnGhost}>
          Cancel
        </button>
        <button type="button" onClick={() => void check()} disabled={!file} className={btnPrimary}>
          Review
        </button>
      </>
    );
  } else if (step === 'checking') {
    body = (
      <div className="space-y-3 py-4">
        <p className="text-sm font-medium text-ink-950">{progress ?? 'Checking…'}</p>
        {partList}
      </div>
    );
    footer = (
      <button type="button" onClick={cancelRun} className={btnGhost}>
        Cancel
      </button>
    );
  } else if (step === 'review') {
    const action = review ? importAction(review) : null;
    const notice = partsNotice(parts.length);
    const shownRows = review ? review.rows.filter((r) => r.outcome === filter) : [];
    const fileIssues = capped(review?.fileIssues ?? []);
    // In a window too short for the table's minimum, the review scrolls as a whole rather than drawing over itself.
    body = (
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {checkFailure ? (
          <>
            <ErrorNotice message={`${checkFailure} Nothing was imported.`} />
            {partList}
          </>
        ) : review ? (
          <>
            {review.template ? <ReadWithLine template={review.template} onOpenTemplates={() => { onClose(); onOpenTemplates(); }} /> : null}
            {notice ? <p className="text-xs text-ink-800/60">{notice}</p> : null}
            <p className="num text-sm text-ink-950">{reviewCountsLine(review)}</p>
            {fileIssues.shown.length > 0 ? (
              // Its own scroll, capped, so twenty issues neither push the table to nothing nor run under the line below.
              <div className="max-h-[25vh] shrink-0 space-y-1 overflow-y-auto">
                {fileIssues.shown.map((issue) => (
                  <p key={`${issue.code}|${issue.message}`} className={issue.level === 'error' ? 'text-sm text-chip-500' : 'text-sm text-ink-800/70'}>
                    {issue.message}
                  </p>
                ))}
                {fileIssues.more ? <p className="text-xs text-ink-800/60">{fileIssues.more}</p> : null}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {reviewFilters(review).map((f) => (
                <button
                  key={f.filter}
                  type="button"
                  onClick={() => setFilter(f.filter)}
                  aria-pressed={filter === f.filter}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    filter === f.filter ? 'border-felt-700 bg-felt-50 text-felt-700' : 'border-ink-800/15 text-ink-800/70'
                  }`}
                >
                  {f.label} · {f.count.toLocaleString('en-US')}
                </button>
              ))}
            </div>
            <div className="min-h-24 flex-1 overflow-auto rounded-lg border border-ink-800/10">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-white text-[11px] uppercase tracking-wide text-ink-800/50">
                  <tr>
                    <th className="px-3 py-2">Row</th>
                    <th className="px-3 py-2">Card</th>
                    <th className="px-3 py-2">Outcome</th>
                    <th className="px-3 py-2">Changes and notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-800/5">
                  {shownRows.map((r) => (
                    <tr key={r.row} className="align-top">
                      <td className="num px-3 py-2 text-ink-800/60">{r.row.toLocaleString('en-US')}</td>
                      <td className="px-3 py-2 font-medium text-ink-950">{r.cardId}</td>
                      <td className="px-3 py-2">{OUTCOME_WORDS[r.outcome] ?? r.outcome}</td>
                      <td className="px-3 py-2">
                        <ul className="space-y-0.5">
                          {rowChanges(r).map((change) => (
                            <li key={change}>{change}</li>
                          ))}
                          {r.issues.map((issue) => (
                            <li key={`${issue.code}|${issue.message}`} className={issue.level === 'error' ? 'text-chip-500' : 'text-ink-800/60'}>
                              {issue.message}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    );
    footer = (
      <>
        <button type="button" onClick={() => setStep('file')} className={btnGhost}>
          Back
        </button>
        {action ? (
          <button
            type="button"
            onClick={() => void save()}
            disabled={action.disabledReason !== null}
            title={action.disabledReason ?? undefined}
            className={btnPrimary}
          >
            {action.label}
          </button>
        ) : null}
      </>
    );
    if (action?.disabledReason) {
      body = (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          {body}
          <p className="shrink-0 text-xs text-ink-800/60">{action.disabledReason}</p>
        </div>
      );
    }
  } else if (step === 'saving') {
    body = (
      <div className="space-y-3 py-4">
        <p className="text-sm font-medium text-ink-950">{stopping ? 'Stopping after this part…' : (progress ?? 'Saving…')}</p>
        {partList}
      </div>
    );
    footer = (
      <button type="button" onClick={cancelRun} disabled={stopping} className={btnGhost}>
        {stopping ? 'Stopping…' : 'Stop after this part'}
      </button>
    );
  } else if (step === 'done' && outcome) {
    body = (
      <div className="space-y-3 py-4">
        {runSummary(runParts, outcome).map((line) => (
          <p key={line} className="text-sm text-ink-950">
            {line}
          </p>
        ))}
        {partList}
      </div>
    );
    footer = (
      <>
        {offersReviewAgain(outcome) ? (
          <button type="button" onClick={() => void check()} className={btnGhost}>
            Review again
          </button>
        ) : null}
        <button type="button" onClick={onClose} className={btnPrimary}>
          Close
        </button>
      </>
    );
  }

  return (
    <Modal
      title="Import players"
      subtitle={[roomName, fileName].filter(Boolean).join(' · ')}
      onClose={close}
      width="max-w-5xl"
      fill={step === 'review'}
      footer={footer}
    >
      <StepBar current={step === 'done' && outcome && !importReached(runParts, outcome) ? 'saving' : step} />
      {step === 'review' ? <div className="flex min-h-0 flex-1 flex-col">{body}</div> : body}
    </Modal>
  );
}
